import React, { Fragment } from 'react';
import {makeStyles, createStyles, useMediaQuery} from '@material-ui/core';
import {Link, Chip, ExtendedTheme, TextBlock, InlineText, Children, useTheme} from 'neon-design-system';
import { useHistory } from 'react-router-dom';
import { sortArray } from '../../utils/sorting.utils';
import { ListItem } from '../../shared/types';
import { ViewTracker } from './ViewTracker';

const useStyles = makeStyles((theme: ExtendedTheme) => createStyles({
	listHorizontal: {
		display: 'flex',
		flexDirection: 'row',
		flexWrap: 'wrap',
		marginTop: -theme.spacing(1)
	},
	oneLine: {
		flexWrap: 'initial'
	},
	listVertical: {
		display: 'flex',
		flexDirection: 'column',
		overflow: 'hidden'
	},
	linkHorizontal: {
		fontSize: 'inherit'
	},
	linkVertical: {
		marginTop: theme.spacing(1),
		fontSize: 'inherit'
	},
	chip: {
		marginRight: theme.spacing(1),
		marginTop: theme.spacing(1)
	},
	spacedVerticalLinks: {
		'&:not(:last-child)': {
			margin: `0 0 ${theme.spacing(2)}px 0`
		}
	}
}));

export enum ListItemOrientation {
	Horizontal,
	Vertical
}

export enum ListItemType {
	Chip,
	Link
}

declare type ChipColors = 'default' | 'error' | 'warning' | 'success' | 'info' | 'neutral' | 'primary' | 'secondary';

export interface LinkListProps {
	items: ListItem[];
	orientation: ListItemOrientation;
	displayType: ListItemType;
	chipVariant?: ChipColors;
	disableSort?: boolean;
	linkClassName?: string;
	isSpacedVerticalLinks?: boolean;
	source?: string;
	isHorizontalListOneLine?: boolean;
	link?: Children;
}


const LinkList = (props: LinkListProps): JSX.Element => {
	const history = useHistory();
	const classes = useStyles();
	const { theme } = useTheme();
	const sm = useMediaQuery(theme.breakpoints.down('sm'));

	const chipClick = (item: ListItem) => {
		history.push(item.url);
	}

	const tmpLink = props.orientation === ListItemOrientation.Horizontal ? classes.linkHorizontal : classes.linkVertical;
	const linkClasses = [tmpLink, props.linkClassName].filter(Boolean).join(' ');

	const sortedItems = getSortedItems(props.items, props.disableSort);

	function getSortedItems(items: ListItem[], disableSort?: boolean): ListItem[] {
		if (!items) {
			return [];
		}

		return disableSort ? items : sortArray(props.items, 'name');
	}

	const clickableItems = sortedItems.map((item, i) => {

		if (props.displayType === ListItemType.Chip) {
			return (
				<Chip
					key={i}
					className={classes.chip}
					label={item.name}
					icon={item.icon}
					styleName={props.chipVariant}
					onClick={() => chipClick(item)}
				/>
			)
		}

		return (
			<Fragment key={i}>
				{props.orientation === ListItemOrientation.Horizontal &&
					i > 0 && <InlineText xs="small" md="medium" xl="large" className={linkClasses}>,&nbsp;</InlineText>}
				<TextBlock className={props.isSpacedVerticalLinks ? classes.spacedVerticalLinks : ''}>
					<ViewTracker source={props.source || ''}><Link className={linkClasses} to={item.url}>{item.name}</Link></ViewTracker>
				</TextBlock>
			</Fragment>
		)
	})

	return (
		<div className={
			[
				props.orientation === ListItemOrientation.Horizontal ?
					classes.listHorizontal : classes.listVertical,
				props.isHorizontalListOneLine && sm && classes.oneLine
			].filter(n => !!n).join(' ')
		}>
			{clickableItems}
			{props.link}
		</div>
	);
};

export default LinkList;
